import React from 'react'

import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { MultipleChoiceElement } from 'modules/forms/lib/elements/MultipleChoiceElement'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'
import { SingleChoiceElement } from 'modules/forms/lib/elements/SingleChoiceElement'

import { OptionElementPreview } from './OptionElementPreview'

const isSectionElement = (element: Element): element is SectionElement => {
  return element instanceof SectionElement
}

const patchSingleElement = (element: Element, showCorrectAnswers: boolean): void => {
  if (element instanceof MultipleChoiceElement || element instanceof SingleChoiceElement) {
    element.getPreviewComponent = (disabled?: boolean, _showCorrectAnswerIcons?: boolean) => {
      return React.createElement(OptionElementPreview, {
        element,
        isSingleChoice: element instanceof SingleChoiceElement,
        disabled,
        showCorrectAnswers,
      })
    }
  }
}

export const patchElementsForPreview = (elements: Element[], showCorrectAnswers = false): void => {
  elements.forEach((element) => {
    if (isSectionElement(element)) {
      element.elements.forEach((nestedElement) => {
        patchSingleElement(nestedElement, showCorrectAnswers)
      })
    } else {
      patchSingleElement(element, showCorrectAnswers)
    }
  })
}

const validateSingleElement = (element: Element): boolean => {
  const userAnswer = element.answer

  if (!element.hasCorrectAnswer) {
    if (userAnswer) {
      userAnswer.isCorrect = true
    }
    return true
  }

  if (!userAnswer || !userAnswer.options || userAnswer.options.length === 0) {
    return false
  }

  const options = element.options || []
  const correctOptionIds = options.flatMap((opt, index) =>
    opt.isCorrect ? [opt.id || `${element.uuid}-${index}`] : []
  )

  const selectedOptionIds = userAnswer.options
  const isElementCorrect =
    selectedOptionIds.length === correctOptionIds.length &&
    selectedOptionIds.every((selectedId) => correctOptionIds.includes(selectedId))

  userAnswer.isCorrect = isElementCorrect

  return isElementCorrect
}

const validateQuizAnswers = (form: Form | undefined): boolean => {
  if (!form) return false

  let allCorrect = true

  form.elements.forEach((element) => {
    if (isSectionElement(element)) {
      element.elements.forEach((nestedElement) => {
        if (!validateSingleElement(nestedElement)) {
          allCorrect = false
        }
      })
    } else {
      if (!validateSingleElement(element)) {
        allCorrect = false
      }
    }
  })

  return allCorrect
}

interface HandleSubmitPreviewModeParams {
  form: Form | undefined
  hasToRetry: boolean
  submissionResult: 'passed' | 'failed' | null
  isCompleted: boolean
  setNumberOfAnswers: (n: number) => void
  setSubmissionResult: (result: 'passed' | 'failed' | null) => void
  setIsRetrying: (retrying: boolean) => void
  updateNextButton: () => void
  scrollToTop: () => void
}

export const handleSubmitPreviewMode = async ({
  form,
  hasToRetry,
  submissionResult,
  isCompleted,
  setNumberOfAnswers,
  setSubmissionResult,
  setIsRetrying,
  updateNextButton,
  scrollToTop,
}: HandleSubmitPreviewModeParams): Promise<boolean> => {
  if (!form) return true

  if (hasToRetry) {
    form.resetFormAnswers()
    setNumberOfAnswers(0)
    setSubmissionResult(null)
    setIsRetrying(true)
    updateNextButton()
    scrollToTop()
    return false
  }

  if (submissionResult !== null || isCompleted) {
    return true
  }

  const allCorrect = validateQuizAnswers(form)
  const result = allCorrect ? 'passed' : 'failed'
  setSubmissionResult(result)
  setIsRetrying(false)
  setNumberOfAnswers(form.numberOfAnswers())

  patchElementsForPreview(form.elements, allCorrect)

  updateNextButton()
  scrollToTop()

  return false
}
